<script setup>
import { ref, onMounted, computed, toRef, watch } from 'vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const userValidation = ref(null);
const validatingUser = ref(false);
const fixingUser = ref(false);

// Use centralized QuePasa service
const quepasaService = window.quepasa?.service;

const isQuePasaInbox = computed(() => {
  return quepasaService?.inbox?.isQuePasa(props.inbox) || false;
});

const shouldShowBanner = computed(() => {
  const show =
    isQuePasaInbox.value &&
    userValidation.value &&
    !userValidation.value.valid &&
    !validatingUser.value &&
    !fixingUser.value;

  return show;
});

const validateQuepasaUser = async () => {
  if (!isQuePasaInbox.value || !quepasaService) return;

  try {
    validatingUser.value = true;
    const accountId = window.location.pathname.split('/')[3];

    const response = await quepasaService.validation.checkUser(accountId);
    userValidation.value = response;
  } catch (error) {
    userValidation.value = null;
  } finally {
    validatingUser.value = false;
  }
};

const fixQuepasaUser = async () => {
  if (!props.inbox?.id || !quepasaService) return;

  try {
    fixingUser.value = true;
    const accountId = window.location.pathname.split('/')[3];

    const response = await quepasaService.validation.fixUser(accountId, props.inbox.id);

    if (response.success) {
      userValidation.value = {
        valid: true,
        user: response.user,
      };
      useAlert('QuePasa user configured successfully!');
    }
  } catch (error) {
    useAlert('Failed to configure QuePasa user');
  } finally {
    fixingUser.value = false;
  }
};

// Watch for inbox changes and validate when it becomes a QuePasa inbox
watch(
  isQuePasaInbox,
  newVal => {
    if (newVal) {
      validateQuepasaUser();
    }
  },
  { immediate: true }
);

onMounted(() => {
  validateQuepasaUser();
});
</script>

<template>
  <div>
    <div v-if="shouldShowBanner" class="mx-8 mt-5">
      <!-- User Validation Error (Critical) - Only show for QuePasa inboxes -->
      <div class="p-4 bg-red-50 border border-red-200 rounded-lg">
        <div class="flex items-start gap-3">
          <fluent-icon
            icon="error-circle"
            size="20"
            class="text-red-600 mt-0.5"
          />
          <div class="flex-1">
            <p class="text-sm font-medium text-red-800 mb-1">
              {{ $t('QUEPASA_INBOX.VALIDATION.USER_NOT_CONFIGURED_TITLE') }}
            </p>
            <p class="text-xs text-red-600 mb-3">
              {{ userValidation.error }}
            </p>
            <NextButton
              size="sm"
              color-scheme="primary"
              :is-loading="fixingUser"
              @click="fixQuepasaUser"
            >
              {{ $t('QUEPASA_INBOX.VALIDATION.CREATE_USER_BUTTON') }}
            </NextButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
